import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useMemo, memo } from 'react';
import { FaRegWindowMaximize, FaQuestionCircle, FaRegSave, FaFilm, } from 'react-icons/fa';
import { Toolbar, useFullscreen } from 'react-science/ui';
import { docsBaseUrl } from '../../constants';
import { useChartData } from '../context/ChartContext';
import { usePreferences, useWorkspacesList, } from '../context/PreferencesContext';
import Button from '../elements/Button';
import { Header } from '../elements/Header';
import DropDownButton from '../elements/dropDownButton/DropDownButton';
import { useSaveSettings } from '../hooks/useSaveSettings';
import { useWorkspaceAction } from '../hooks/useWorkspaceAction';
import { LogsHistoryModal } from '../modal/LogsHistoryModal';
import AboutUsModal from '../modal/aboutUs/AboutUsModal';
import GeneralSettingsModal from '../modal/setting/GeneralSettings';
import WorkspaceItem from '../modal/setting/WorkspaceItem';
import { options } from '../toolbar/ToolTypes';
import ApodizationOptionsPanel from './ApodizationOptionsPanel';
import AutoPeakPickingOptionPanel from './AutoPeakPickingOptionPanel';
import BaseLineCorrectionPanel from './BaseLineCorrectionPanel';
import { HeaderContainer } from './HeaderContainer';
import PhaseCorrectionPanel from './PhaseCorrectionPanel';
import PhaseCorrectionTwoDimensionsPanel from './PhaseCorrectionTwoDimensionsPanel';
import RangesPickingOptionPanel from './RangesPickingOptionPanel';
import ZeroFillingOptionsPanel from './ZeroFillingOptionsPanel';
import Zones2DOptionPanel from './Zones2DOptionPanel';
export const headerLabelStyle = {
    label: {
        fontWeight: 'normal',
        fontSize: '12px',
    },
    wrapper: {
        paddingRight: '5px',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
    },
};
const styles = css `
  container-type: inline-size;
  z-index: 1;

  @container (max-width:1200px) {
    .small-label {
      display: block;
    }

    .large-label {
      display: none !important;
    }
  }

  @container (min-width:1200px) {
    .small-label {
      display: none;
    }

    .large-label {
      display: block;
    }
  }
`;
function HeaderInner(props) {
    const { selectedOptionPanel, height } = props;
    const { current: { display: { general }, }, workspace, } = usePreferences();
    const { setActiveWorkspace } = useWorkspaceAction();
    const fullscreen = useFullscreen();
    const workspacesList = useWorkspacesList();
    const hideGeneralSettings = !!(general?.hideGeneralSettings && workspace.base);
    const hideWorkspaces = !!(general?.hideWorkspaces && workspace.base);
    const selectedPanel = useMemo(() => {
        switch (selectedOptionPanel) {
            case options.apodization.id:
                return _jsx(ApodizationOptionsPanel, {});
            case options.zeroFilling.id:
                return _jsx(ZeroFillingOptionsPanel, {});
            case options.phaseCorrection.id:
                return _jsx(PhaseCorrectionPanel, {});
            case options.phaseCorrectionTwoDimensions.id:
                return _jsx(PhaseCorrectionTwoDimensionsPanel, {});
            case options.peakPicking.id:
                return _jsx(AutoPeakPickingOptionPanel, {});
            case options.rangePicking.id:
                return _jsx(RangesPickingOptionPanel, {});
            case options.baselineCorrection.id:
                return _jsx(BaseLineCorrectionPanel, {});
            case options.zonePicking.id:
                return _jsx(Zones2DOptionPanel, {});
            default:
                return null;
        }
    }, [selectedOptionPanel]);
    function changeWorkspaceHandler(option) {
        setActiveWorkspace(option.key);
    }
    function renderItem(item) {
        return _jsx(WorkspaceItem, { item: item });
    }
    return (_jsx("div", { css: styles, children: _jsxs(Header, { style: { leftStyle: { flex: 1 }, containerStyle: { padding: '2px' } }, children: [_jsxs(HeaderContainer, { style: {
                        alignItems: 'center',
                    }, children: [_jsx("div", { children: _jsx(Toolbar, { children: _jsx(AboutUsModal, {}) }) }), _jsx("div", { className: "toolOptionsPanel", style: { flex: 1 }, children: selectedPanel })] }), _jsxs(HeaderContainer, { style: {
                        alignItems: 'center',
                    }, children: [!hideWorkspaces && (_jsx(DropDownButton, { data: workspacesList, selectedKey: workspace.current, onSelect: changeWorkspaceHandler, renderItem: renderItem })), !hideGeneralSettings && _jsx(SaveButton, {}), !general?.hideLogs && _jsx(LogsHistoryModal, {}), _jsx("div", { children: _jsxs(Toolbar, { children: [!general?.hideHelp && (_jsx(Toolbar.Item, { id: "user-manual", tooltip: "User manual", onClick: () => window.open(docsBaseUrl, '_blank'), icon: _jsx(FaQuestionCircle, {}) })), _jsx(Toolbar.Item, { id: "user-manual", tooltip: "NMRium channel", onClick: () => window.open('https://www.youtube.com/@nmrium', '_blank'), icon: _jsx(FaFilm, {}) }), !hideGeneralSettings && (_jsx(GeneralSettingsModal, { height: height / 2 })), !fullscreen.isFullScreen && !general?.hideMaximize && (_jsx(Toolbar.Item, { id: "full-screen", onClick: fullscreen.toggle, tooltip: "Full screen", className: "windowButton", icon: _jsx(FaRegWindowMaximize, {}) }))] }) })] })] }) }));
}
function SaveButton() {
    const { workspace, workspaces, originalWorkspaces } = usePreferences();
    const { saveSettings, SaveSettingsModal } = useSaveSettings();
    const isWorkspaceHasSettingNotSaved = JSON.stringify(workspaces[workspace.current]) !==
        JSON.stringify(originalWorkspaces[workspace.current]);
    function handleSave() {
        saveSettings();
    }
    return (_jsxs(_Fragment, { children: [_jsx(Button.Done, { wrapperClassName: "small-width-none", onClick: handleSave, fill: "clear", style: { fontSize: '1.4em', marginLeft: '5px' }, ...(!isWorkspaceHasSettingNotSaved && {
                    color: { base: 'gray', hover: 'gray' },
                    backgroundColor: { base: 'gray', hover: 'lightgray' },
                    disabled: true,
                }), toolTip: "Save workspace locally in the browser", children: _jsx(FaRegSave, {}) }), _jsx(SaveSettingsModal, {})] }));
}
const MemoizedHeader = memo(HeaderInner);
export default function HeaderWrapper() {
    const { toolOptions: { selectedOptionPanel }, height, } = useChartData();
    return (_jsx(MemoizedHeader, { selectedOptionPanel,
        height }));
}
